import React from 'react';

/**
 * Datenschutz- und Privacy-Policy-Komponente.
 *
 * Diese Komponente enthält sowohl die deutsche als auch die englische Datenschutzerklärung
 * für die NumSolve-Projektgruppe. Die Texte wurden auf den aktuellen
 * Rechtsstand (DSGVO und TDDDG, vormals TTDSG) angepasst und spiegeln wider,
 * dass derzeit ausschließlich technisch notwendige Cookies eingesetzt werden.
 * Bei Änderungen der Datenverarbeitung sollten die Texte entsprechend
 * überarbeitet werden.
 */

const GermanContent = () => (
  <div className="space-y-4">
    <h1 className="text-3xl font-bold">Datenschutzerklärung</h1>

    <p>
      Diese Website wird von der NumSolve‑Projektgruppe betrieben (im Folgenden „wir“ oder „uns“).
      Fragen zum Datenschutz oder zur Datensicherheit können Sie jederzeit an
      <span className="underline"> info@numsolve.org</span> richten.
    </p>

    <h2 className="text-2xl font-bold mt-6">1. Verantwortlicher</h2>
    <p>
      Verantwortlicher im Sinne der DSGVO und des&nbsp;
      <abbr title="Telekommunikation‑Digitale‑Dienste‑Datenschutz‑Gesetz">TDDDG</abbr>
      &nbsp;(vormals TTDSG) ist:
      <br />
      <strong>Name/Organisation:</strong> NumSolve‑Projektgruppe
      <br />
      <strong>Anschrift/E‑Mail:</strong> info@numsolve.org
    </p>

    <h2 className="text-2xl font-bold mt-6">2. Verarbeitete Datenarten und Zwecke</h2>
    <table className="w-full border border-gray-300 text-left">
      <thead>
        <tr className="bg-gray-100">
          <th className="p-2 border-b border-gray-300">Datenkategorie</th>
          <th className="p-2 border-b border-gray-300">Beschreibung &amp; Zweck</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="p-2 align-top border-b border-gray-300">
            Technisch notwendige Cookies/Local‑Storage
          </td>
          <td className="p-2 border-b border-gray-300">
            Speichern, ob Sie z.&nbsp;B. das Cookie‑Banner bestätigt haben. Diese Cookies
            enthalten keine personenbezogenen Daten und dienen ausschließlich der Sicherstellung
            grundlegender Funktionen. Derzeit setzen wir nur einen Eintrag zur Speicherung Ihrer
            Auswahl im Consent‑Banner ein.
          </td>
        </tr>
        <tr>
          <td className="p-2 align-top border-b border-gray-300">Berechnungs‑Logs</td>
          <td className="p-2 border-b border-gray-300">
            Bei Nutzung unserer Rechenfunktionen (z.&nbsp;B. Newton-Verfahren, Interpolation,
            Matrixzerlegung) werden pseudonymisierte oder anonymisierte Eingabedaten
            (z.&nbsp;B. Matrizen, Iterationsschritte) serverseitig gespeichert. Zweck ist die
            Fehleranalyse, Debugging und Verbesserung der Algorithmen.
          </td>
        </tr>
        <tr>
          <td className="p-2 align-top border-b border-gray-300">Server‑/Verbindungsdaten</td>
          <td className="p-2 border-b border-gray-300">
            Bei jedem Aufruf übermittelt Ihr Browser automatisch Informationen wie IP‑Adresse,
            Datum/Uhrzeit, Browsertyp und Betriebssystem. Diese Daten werden für den
            reibungslosen Betrieb sowie zur Abwehr von Angriffen kurzfristig gespeichert und
            anschließend gelöscht oder anonymisiert.
          </td>
        </tr>
        <tr>
          <td className="p-2 align-top">Kommunikationsdaten (bei Kontaktaufnahme)</td>
          <td className="p-2">
            Wenn Sie uns per E‑Mail kontaktieren oder das Kontaktformular nutzen, verarbeiten
            wir die von Ihnen mitgeteilten Daten (z.&nbsp;B. Name, E‑Mail‑Adresse, Inhalt der
            Nachricht) zur Bearbeitung Ihrer Anfrage. Diese Daten werden nur für den Zweck der
            Korrespondenz verwendet.
          </td>
        </tr>
      </tbody>
    </table>

    <p>
      Wir nutzen keine externen Analyse‑ oder Tracking‑Tools, keine Social‑Media‑Plugins und
      keine extern eingebundenen Fonts; sämtliche Schriftarten liegen lokal vor.
    </p>

    <h2 className="text-2xl font-bold mt-6">3. Rechtsgrundlagen</h2>
    <ul className="list-disc list-inside">
      <li>
        Art.&nbsp;6&nbsp;Abs.&nbsp;1&nbsp;lit.&nbsp;f DSGVO – berechtigtes Interesse an der
        Verarbeitung technischer Daten zur Gewährleistung von Sicherheit und Funktionsfähigkeit.
      </li>
      <li>
        Art.&nbsp;6&nbsp;Abs.&nbsp;1&nbsp;lit.&nbsp;b DSGVO – Bearbeitung Ihrer Anfragen, sofern diese
        auf einen Vertrag oder vorvertragliche Maßnahmen abzielen.
      </li>
      <li>
        Art.&nbsp;6&nbsp;Abs.&nbsp;1&nbsp;lit.&nbsp;a DSGVO – Einwilligung für Verarbeitungen, die nicht
        zwingend erforderlich sind; eine Einwilligung wird vor Verarbeitung eingeholt, sofern
        erforderlich.
      </li>
    </ul>

    <h2 className="text-2xl font-bold mt-6">4. Cookies und Einwilligung</h2>
    <p>
      Wir verwenden ausschließlich technisch notwendige Cookies bzw. Local‑Storage‑Einträge
      (derzeit nur zum Speichern Ihrer Auswahl im Consent‑Banner). Sollten zukünftig weitere
      Cookies eingeführt werden, informieren wir Sie und holen ggf. Ihre Einwilligung ein. Eine
      erteilte Einwilligung können Sie jederzeit mit Wirkung für die Zukunft widerrufen.
    </p>

    <h2 className="text-2xl font-bold mt-6">5. Speicherdauer</h2>
    <ul className="list-disc list-inside">
      <li>
        Berechnungs‑Logs: spätestens 30&nbsp;Tage nach Analyse oder Fehlerbehebung.
      </li>
      <li>
        Kommunikationsdaten: bis zur abschließenden Bearbeitung Ihres Anliegens; bei gesetzlichen
        Aufbewahrungspflichten erfolgt eine Einschränkung der Verarbeitung.
      </li>
      <li>
        Server‑/Verbindungsdaten: in der Regel 7&nbsp;Tage; anschließend Löschung oder
        Anonymisierung.
      </li>
      <li>
        Technisch notwendige Cookies: bis Sie diese in Ihrem Browser löschen oder nach Ablauf durch
        den Browser entfernt werden.
      </li>
    </ul>

    <h2 className="text-2xl font-bold mt-6">6. Ihre Rechte</h2>
    <p>
      Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung,
      Datenübertragbarkeit, Widerspruch gegen Verarbeitungen auf Basis berechtigter Interessen,
      Widerruf erteilter Einwilligungen sowie Beschwerde bei einer Aufsichtsbehörde. Zur Ausübung
      Ihrer Rechte genügt eine formlose Mitteilung an die oben angegebene Adresse.
    </p>

    <h2 className="text-2xl font-bold mt-6">7. Sicherheit</h2>
    <p>
      Wir treffen technische und organisatorische Maßnahmen gemäß Art.&nbsp;32&nbsp;DSGVO, um Ihre
      Daten vor Verlust, Missbrauch oder unbefugtem Zugriff zu schützen. Dazu zählen TLS/SSL‑
      Verschlüsselung, regelmäßige Updates, Code‑Reviews und automatisierte Tests,
      Sicherheits‑Scanner, Rate‑Limiting, Firewalls sowie beschränkte Zugriffsrechte auf Logs.
    </p>

    <h2 className="text-2xl font-bold mt-6">8. Drittanbieter &amp; Links</h2>
    <p>
      Wir übermitteln keine personenbezogenen Daten an Dritte und binden keine externen Skripte oder Fonts ein. Beim Besuch
      externer Links gelten die Datenschutzhinweise der jeweiligen Anbieter.
    </p>

    <h2 className="text-2xl font-bold mt-6">9. Daten von Minderjährigen</h2>
    <p>
      Unser Angebot richtet sich an volljährige Nutzer. Wir erheben nicht wissentlich
      personenbezogene Daten von Kindern unter 16 Jahren und löschen entsprechende Daten nach
      Kenntniserlangung umgehend.
    </p>

    <h2 className="text-2xl font-bold mt-6">10. Aktualisierung</h2>
    <p>
      Diese Datenschutzerklärung kann künftig angepasst werden, um geänderte rechtliche
      Rahmenbedingungen (z.&nbsp;B. Änderungen der DSGVO oder des TDDDG) oder Funktionsänderungen
      abzubilden. Bitte informieren Sie sich regelmäßig über den aktuellen Stand.
    </p>
  </div>
);

const EnglishContent = () => (
  <div className="space-y-4">
    <h1 className="text-3xl font-bold">Privacy Policy</h1>

    <p>
      This website is operated by the NumSolve project group (“we” or “us”). For questions about
      data protection or security, contact
      <span className="underline"> info@numsolve.org</span>.
    </p>

    <h2 className="text-2xl font-bold mt-6">1. Controller</h2>
    <p>
      Controller pursuant to the GDPR and the&nbsp;
      <abbr title="Telecommunications Digital Services Data Protection Act">TDDDG</abbr>
      &nbsp;(formerly TTDSG):
      <br />
      <strong>Name/Organisation:</strong> NumSolve project group
      <br />
      <strong>Address/E‑mail:</strong> info@numsolve.org
    </p>

    <h2 className="text-2xl font-bold mt-6">2. Data categories and purposes</h2>
    <table className="w-full border border-gray-300 text-left">
      <thead>
        <tr className="bg-gray-100">
          <th className="p-2 border-b border-gray-300">Data category</th>
          <th className="p-2 border-b border-gray-300">Description &amp; purpose</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="p-2 align-top border-b border-gray-300">
            Technically necessary cookies/local storage
          </td>
          <td className="p-2 border-b border-gray-300">
            We store, for example, whether you accepted the cookie banner. These entries contain no
            personal data and solely ensure basic website functions. Currently, we only use one
            entry to record your consent choice.
          </td>
        </tr>
        <tr>
          <td className="p-2 align-top border-b border-gray-300">Calculation logs</td>
          <td className="p-2 border-b border-gray-300">
            When using our calculation features (e.g. Newton’s method, interpolation, matrix
            decomposition), pseudonymised or anonymised input data (e.g. matrices, iteration steps)
            may be stored on the server for debugging, algorithm improvement and error analysis.
          </td>
        </tr>
        <tr>
          <td className="p-2 align-top border-b border-gray-300">Server/connection data</td>
          <td className="p-2 border-b border-gray-300">
            Each access automatically transmits information such as IP address, date/time, browser
            type and operating system. These data are briefly stored to ensure smooth operation and
            defend against attacks, then deleted or anonymised.
          </td>
        </tr>
        <tr>
          <td className="p-2 align-top">Communication data (when contacting us)</td>
          <td className="p-2">
            If you contact us by e‑mail or via the contact form, we process the data you provide
            (e.g. name, e‑mail address, message content) to handle your request. These data are used
            solely for correspondence.
          </td>
        </tr>
      </tbody>
    </table>

    <p>
      We do not use external analytics or tracking tools, social media plug‑ins or externally
      hosted fonts; all fonts are served locally.
    </p>

    <h2 className="text-2xl font-bold mt-6">3. Legal bases</h2>
    <ul className="list-disc list-inside">
      <li>
        Art.&nbsp;6(1)(f) GDPR – legitimate interest in processing technical data to ensure security
        and functionality.
      </li>
      <li>
        Art.&nbsp;6(1)(b) GDPR – processing your enquiries provided they relate to a contract or
        pre‑contractual measures.
      </li>
      <li>
        Art.&nbsp;6(1)(a) GDPR – consent for processing not strictly necessary; such consent will be
        obtained before processing where required.
      </li>
    </ul>

    <h2 className="text-2xl font-bold mt-6">4. Cookies and consent</h2>
    <p>
      We only use technically necessary cookies or local storage entries (currently solely to
      record your consent selection in our cookie banner). Should additional cookies be introduced
      in the future, we will inform you and obtain consent where required. You may withdraw
      consent at any time with future effect.
    </p>

    <h2 className="text-2xl font-bold mt-6">5. Retention periods</h2>
    <ul className="list-disc list-inside">
      <li>Calculation logs: deleted no later than 30 days after analysis or error resolution.</li>
      <li>
        Communication data: retained until your request has been fully processed; where statutory
        retention periods apply, processing is restricted.
      </li>
      <li>Server/connection data: usually 7 days; thereafter deletion or anonymisation.</li>
      <li>
        Technically necessary cookies: remain on your device until you delete them manually or
        your browser removes them after expiry.
      </li>
    </ul>

    <h2 className="text-2xl font-bold mt-6">6. Your rights</h2>
    <p>
      You have the right to access, rectification, erasure, restriction of processing, data
      portability, objection to processing based on legitimate interests, withdrawal of consent,
      and to lodge a complaint with a supervisory authority. To exercise your rights, simply
      contact us at the address above.
    </p>

    <h2 className="text-2xl font-bold mt-6">7. Security</h2>
    <p>
      We implement technical and organisational measures pursuant to Art.&nbsp;32 GDPR to protect
      your data from loss, misuse or unauthorised access. These measures include TLS/SSL
      encryption, regular updates, code reviews and automated tests, security scanners, rate
      limiting, firewalls, and restricted access to logs.
    </p>

    <h2 className="text-2xl font-bold mt-6">8. Third‑party services &amp; links</h2>
    <p>
      We do not transfer personal data to third parties or embed external scripts or fonts. When visiting external links, please note the
      respective providers’ privacy policies.
    </p>

    <h2 className="text-2xl font-bold mt-6">9. Data from minors</h2>
    <p>
      Our offering targets adults. We do not knowingly collect personal data from children under
      16 and delete such data immediately upon discovery.
    </p>

    <h2 className="text-2xl font-bold mt-6">10. Updates</h2>
    <p>
      We may update this privacy policy to reflect changes in legal requirements (e.g. amendments
      to the GDPR or TDDDG) or our services. Please check this page regularly for the current
      version.
    </p>
  </div>
);

/**
 * Hauptkomponente für die Anzeige der Datenschutzerklärung in der gewünschten Sprache.
 *
 * @param {{ language?: 'de' | 'en' }} props
 *   Falls `language` auf 'de' gesetzt wird, wird die deutsche Version angezeigt,
 *   andernfalls die englische.
 */
const Privacy = ({ language = 'de' }) => (
  <div className="max-w-3xl mx-auto p-8 text-gray-800">
    {language === 'de' ? <GermanContent /> : <EnglishContent />}
  </div>
);

export default Privacy;